/**
 * eventSchema — the wire boundary. Zod-validated, envelope-first, and it returns
 * the ORIGINAL object reference.
 *
 * Two rules, each paying for a failure that already happened once.
 */

import { z } from 'zod';

const WireId = z.union([z.string(), z.number()]);

/**
 * The wire envelope. Every field is optional except the type, and `data` is
 * deliberately unshaped — this layer must not know a domain's payload.
 */
export const WireEnvelopeSchema = z
  .object({
    event_type: z.string().optional(),
    event: z.string().optional(),
    time: z.string().optional(),
    rid: WireId.optional(),
    chat_id: WireId.optional(),
    userid: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional()
  })
  .loose();

export interface WireEnvelope {
  event_type?: string;
  event?: string;
  time?: string;
  rid?: string | number;
  chat_id?: string | number;
  userid?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Alias. `ChatTransport`, `MockTransport`, `ManagedConnection` and
 * `streamRunner` all type on this name.
 */
export type PubsubEventPayload = WireEnvelope;

/**
 * The end-of-turn payload — i.e. `io.doclm.run.completed`'s `data`.
 */
export interface ChatMetaEventPayload {
  response?: string;
  files?: unknown[];
  quoted_source?: unknown[];
  source?: unknown[];
  generated_files?: unknown[];
  additional_steps?: unknown;
  chat_subject?: string;
  type?: string;
  step_name?: string;
  rid?: string | number;
  [key: string]: unknown;
}

export interface ValidationResult {
  ok: boolean;
  data?: WireEnvelope;
  error?: z.ZodError;
}

/**
 * Validate one decoded frame. Name and result shape preserved, so every caller is
 * unchanged.
 */
export function validatePubsubEvent(input: unknown): ValidationResult {
  const result = WireEnvelopeSchema.safeParse(input);
  if (!result.success) {
    return { ok: false, error: result.error };
  }

  if (
    typeof result.data.event_type !== 'string' &&
    typeof result.data.event !== 'string'
  ) {
    return { ok: false };
  }

  return { ok: true, data: input as WireEnvelope };
}
